// File: src/components/season/SeasonDetailPage.js
import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import AddTeamModal from "../teams/AddTeamModal";
import AddLocationModal from "../season/AddLocationModal";
import AddGameModal from "../games/AddGameModal";
import EditGameModal from "../games/EditGameModal";
import SeasonScheduleWizard from "./SeasonScheduleWizard";
import AddEventModal from "./AddEventModal";
import EditEventModal from "./EditEventModal";
import AddSponsorModal from "./AddSponsorModal";
import EditSponsorModal from "./EditSponsorModal";
import EditRegistrationModal from "./EditRegistrationModal";
import EditSeasonLocksModal from "./EditSeasonLocksModal";
import useUserRoles from '../../hooks/useUserRoles';
import useDebounce from '../utils/debouncUtil';
import truncateString from '../utils/truncateUtil';

// Formats a "YYYY-MM-DD" date string for display without shifting a day due to UTC parsing.
const formatDateForDisplay = (dateStr) => {
	if (!dateStr) return null;
	const [year, month, day] = dateStr.split('-').map(Number);
	if (!year || !month || !day) return dateStr;
	return new Date(year, month - 1, day).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
};

function SeasonDetailPage({
	activeLeague,
	setActiveLeague,
	activeSeason,
	setActiveSeason,
	seasonId: seasonIdFromProp,
	onBack: onBackFromProp,
}) {
	const { isCommissioner } = useUserRoles();

	const { seasonId: paramSeasonId } = useParams();
	const parsedRouteSeasonId =
		paramSeasonId != null && String(paramSeasonId).trim() !== ''
			? Number.parseInt(String(paramSeasonId), 10)
			: NaN;
	const seasonId = Number.isFinite(parsedRouteSeasonId)
		? parsedRouteSeasonId
		: seasonIdFromProp != null && !Number.isNaN(Number(seasonIdFromProp))
			? Number(seasonIdFromProp)
			: activeSeason?.id;
	const navigate = useNavigate();

	// Sync activeSeason global state when the URL (or inline prop) targets a specific season
	useEffect(() => {
		const id = Number.isFinite(parsedRouteSeasonId)
			? parsedRouteSeasonId
			: seasonIdFromProp != null && !Number.isNaN(Number(seasonIdFromProp))
				? Number(seasonIdFromProp)
				: null;
		if (id == null || activeSeason?.id === id) return;
		axios
			.get(`/api/leagues/seasons/${id}/`, {
				headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
			})
			.then((response) => {
				setActiveSeason(response.data);
			})
			.catch(() => { });
	}, [parsedRouteSeasonId, seasonIdFromProp, activeSeason?.id, setActiveSeason]);

	// State for API data, inner tab control, etc.
	const [teams, setTeams] = useState([]);
	const [games, setGames] = useState([]);
	const [events, setEvents] = useState([]);
	const [sponsors, setSponsors] = useState([]);
	const [seasonDetails, setSeasonDetails] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	// const [isChecked, setIsChecked] = useState(false);
	const [modalVisible, setModalVisible] = useState(false);

	const [showLocationModal, setShowLocationModal] = useState(false);

	const [showGameModal, setShowGameModal] = useState(false);
	const [showEditGameModal, setShowEditGameModal] = useState(false);
	const [selectedGame, setSelectedGame] = useState(null);

	const [showScheduleWizard, setShowScheduleWizard] = useState(false);
	const [showEventModal, setShowEventModal] = useState(false);
	const [showEditEventModal, setShowEditEventModal] = useState(false);
	const [selectedEvent, setSelectedEvent] = useState(null);

	const [showSponsorModal, setShowSponsorModal] = useState(false);
	const [showEditSponsorModal, setShowEditSponsorModal] = useState(false);
	const [selectedSponsor, setSelectedSponsor] = useState(null);

	const [showRegistrationModal, setShowRegistrationModal] = useState(false);
	const [showLocksModal, setShowLocksModal] = useState(false);

	// Game type filter state
	const [gameTypeFilter, setGameTypeFilter] = useState('all');

	// Controlled state for inner tab navigation; defaults to "Teams"
	const [innerActiveTab, setInnerActiveTab] = useState("Teams");

	// Utility function to sort games by scheduled_date
	const sortGamesByDate = (gamesArray) => {
		return gamesArray.sort((a, b) => {
			const dateA = new Date(a.scheduled_date);
			const dateB = new Date(b.scheduled_date);
			return dateA - dateB;
		});
	};

	// Utility function to sort events by datetime
	const sortEventsByDate = (eventsArray) => {
		return eventsArray.sort((a, b) => {
			const dateA = new Date(a.datetime);
			const dateB = new Date(b.datetime);
			return dateA - dateB;
		});
	};

	// Search and pagination state for Players tab
	const [searchTerm, setSearchTerm] = useState('');
	const [filteredPlayers, setFilteredPlayers] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [totalPages, setTotalPages] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const searchTimeoutRef = useRef(null);
	const debouncedSearchTerm = useDebounce(searchTerm, 300);

	useEffect(() => {
		if (seasonId == null) return;
		// Season-scoped endpoint so games match the season from the URL / list, not default activeLeague
		axios
			.get(`/api/games/seasons/${seasonId}/`, {
				headers: {
					Authorization: `Bearer ${localStorage.getItem('access_token')}`,
				},
			})
			.then((response) => {
				const sortedGames = sortGamesByDate(response.data);
				setGames(sortedGames);
			})
			.catch((error) => {
				console.error('Error fetching games:', error);
				alert('Unable to fetch games.');
			});
	}, [seasonId]);

	useEffect(() => {
		// Fetch events for the season
		axios
			.get(`/api/leagues/events/?season_id=${seasonId}`, {
				headers: {
					Authorization: `Bearer ${localStorage.getItem('access_token')}`,
				},
			})
			.then((response) => {
				// Sort events by datetime (nearest to furthest)
				const sortedEvents = sortEventsByDate(response.data);
				setEvents(sortedEvents);
			})
			.catch((error) => {
				// Error fetching events - don't show alert as they might not exist yet
			});
	}, [seasonId]);

	useEffect(() => {
		// Fetch sponsors for the season
		axios
			.get(`/api/leagues/sponsors/?season_id=${seasonId}`, {
				withCredentials: true,
			})
			.then((response) => {
				setSponsors(response.data);
			})
			.catch((error) => {
				// Error fetching sponsors - don't show alert as they might not exist yet
			});
	}, [seasonId]);

	useEffect(() => {
		axios
			.get(`/api/leagues/seasons/${seasonId}/`, {
				headers: {
					Authorization: `Bearer ${localStorage.getItem("access_token")}`,
				},
			})
			.then((response) => {
				setSeasonDetails(response.data);
				setLoading(false);
			})
			.catch((error) => {
				console.error("Error fetching season details:", error);
				setError("Failed to load season details.");
				setLoading(false);
			});

		axios
			.get(`/api/teams/${seasonId}/teams/`, {
				headers: {
					Authorization: `Bearer ${localStorage.getItem("access_token")}`,
				},
			})
			.then((response) => {
				setTeams(response.data);
			})
			.catch((error) => {
				console.error("Error fetching teams:", error);
				alert("Unable to fetch teams.");
			});
	}, [seasonId]);

	// Filter games by game type
	const filteredGames = games.filter(game => {
		if (gameTypeFilter === 'all') return true;
		return game.game_type === gameTypeFilter;
	});


	// Filter and paginate players based on search term
	useEffect(() => {
		if (!seasonDetails?.participants) return;

		const filtered = seasonDetails.participants.filter(player => {
			const fullName = `${player.first_name} ${player.last_name}`.toLowerCase();
			const email = (player.email || '').toLowerCase();
			const phone = (player.phone_number || '').toLowerCase();
			const searchLower = debouncedSearchTerm.toLowerCase();

			return fullName.includes(searchLower) ||
				email.includes(searchLower) ||
				phone.includes(searchLower);
		});

		setFilteredPlayers(filtered);
		setTotalCount(filtered.length);
		setTotalPages(Math.ceil(filtered.length / pageSize));
		setCurrentPage(1);
	}, [debouncedSearchTerm, seasonDetails?.participants, pageSize]);

	// Get current page players
	const getCurrentPagePlayers = () => {
		const startIndex = (currentPage - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		return filteredPlayers.slice(startIndex, endIndex);
	};

	const handleSearch = (e) => {
		const value = e.target.value;
		setSearchTerm(value);
		setCurrentPage(1);

		// Clear any existing timeout
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
		}

		// Set new timeout for immediate visual feedback
		searchTimeoutRef.current = setTimeout(() => {
			// This is handled by the debounced search term
		}, 300);
	};

	const handlePageSizeChange = (e) => {
		setPageSize(Number(e.target.value));
		setCurrentPage(1);
	};

	const handlePageChange = (newPage) => {
		setCurrentPage(newPage);
	};

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}
		};
	}, []);

	if (loading) {
		return <div className="container">Loading season details...</div>;
	}
	if (error) {
		return <div className="container text-danger">{error}</div>;
	}

	const handleShow = () => setModalVisible(true);
	const handleClose = () => setModalVisible(false);
	// const handleCheckboxChange = () => setIsChecked(!isChecked);

	const handleShowLocationModal = () => setShowLocationModal(true);
	const handleCloseLocationModal = () => setShowLocationModal(false);

	const handleShowGameModal = () => setShowGameModal(true);
	const handleCloseGameModal = () => setShowGameModal(false);

	const handleShowEditGameModal = (game) => {
		setSelectedGame(game);
		setShowEditGameModal(true);
	};
	const handleCloseEditGameModal = () => {
		setShowEditGameModal(false);
		setSelectedGame(null);
	};

	const handleShowScheduleWizard = () => setShowScheduleWizard(true);
	const handleCloseScheduleWizard = () => setShowScheduleWizard(false);

	const handleShowEventModal = () => setShowEventModal(true);
	const handleCloseEventModal = () => setShowEventModal(false);

	const handleShowEditEventModal = (event) => {
		setSelectedEvent(event);
		setShowEditEventModal(true);
	};
	const handleCloseEditEventModal = () => {
		setShowEditEventModal(false);
		setSelectedEvent(null);
	};

	const handleShowSponsorModal = () => setShowSponsorModal(true);
	const handleCloseSponsorModal = () => setShowSponsorModal(false);

	const handleShowEditSponsorModal = (sponsor) => {
		setSelectedSponsor(sponsor);
		setShowEditSponsorModal(true);
	};
	const handleCloseEditSponsorModal = () => {
		setShowEditSponsorModal(false);
		setSelectedSponsor(null);
	};

	const handleShowRegistrationModal = () => setShowRegistrationModal(true);
	const handleCloseRegistrationModal = () => setShowRegistrationModal(false);

	const handleShowLocksModal = () => setShowLocksModal(true);
	const handleCloseLocksModal = () => setShowLocksModal(false);

	const handleSeasonSettingsUpdated = (updatedSeason) => {
		setSeasonDetails(updatedSeason);
	};

	const handleLocationAdded = (newLocation) => {
		setSeasonDetails((prev) => ({
			...prev,
			locations: prev.locations ? [...prev.locations, newLocation] : [newLocation],
		}));
	};

	// Navigate back to Settings with the Leagues tab active.
	const handleBack = () => navigate("/settings?active=6");

	const handleTeamAdded = (newTeam) => {
		setTeams((prevTeams) => [...prevTeams, newTeam]);
	};

	const handleGameAdded = (newGame) => {
		setGames((prevGames) => [...prevGames, newGame]);
	};

	const handleGameUpdated = () => {
		// Refresh the games list to get updated data
		axios
			.get(`/api/games/seasons/${seasonId}/`, {
				headers: {
					Authorization: `Bearer ${localStorage.getItem('access_token')}`,
				},
			})
			.then((response) => {
				// Sort games by scheduled_date (nearest to furthest)
				const sortedGames = sortGamesByDate(response.data);
				setGames(sortedGames);
			})
			.catch((error) => {
				console.error('Error fetching updated games:', error);
			});
	};

	const handleScheduleGenerated = (newGames) => {
		// Instead of using the simplified response, fetch the complete game data
		axios
			.get(`/api/games/seasons/${seasonId}/`, {
				headers: {
					Authorization: `Bearer ${localStorage.getItem('access_token')}`,
				},
			})
			.then((response) => {
				// Sort games by scheduled_date (nearest to furthest)
				const sortedGames = sortGamesByDate(response.data);
				setGames(sortedGames);
			})
			.catch((error) => {
				console.error('Error fetching complete games data:', error);
				// Fallback to using the simplified response if fetch fails
				setGames(newGames);
			});

		// Refresh season details to get updated schedule_lock status
		axios
			.get(`/api/leagues/seasons/${seasonId}/`, {
				headers: {
					Authorization: `Bearer ${localStorage.getItem('access_token')}`,
				},
			})
			.then((response) => {
				setSeasonDetails(response.data);
			})
			.catch((error) => {
				console.error("Error refreshing season details:", error);
			});
	};

	// Escape a value for safe inclusion in a CSV cell.
	const escapeCsv = (value) => {
		const str = value == null ? '' : String(value);
		// Quote if the field contains comma, quote, or newline; double up quotes.
		return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
	};

	// Export the full season schedule (all game types) to a downloadable CSV.
	const handleExportSchedule = () => {
		if (!games.length) {
			alert('No games to export.');
			return;
		}

		// Anchor for "Week N" numbering.
		const seasonStart = seasonDetails?.start_date ? new Date(seasonDetails.start_date) : null;
		const weekOf = (game) => {
			if (!seasonStart || !game.scheduled_date) return '';
			const diffDays = Math.floor(
				(new Date(game.scheduled_date) - seasonStart) / (1000 * 60 * 60 * 24)
			);
			return diffDays >= 0 ? Math.floor(diffDays / 7) + 1 : 1;
		};

		// Sort chronologically (games state is already date-sorted, but be defensive).
		const sorted = [...games].sort(
			(a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date)
		);

		const header = ['Week', 'Date', 'Day', 'Time Slot', 'Home Team', 'Away Team', 'Place', 'Game Type'];
		const rows = sorted.map((game) => {
			const home = game.teams?.find((p) => p.is_home);
			const away = game.teams?.find((p) => p.is_away);
			const isBye = game.game_type === 'bye';
			return [
				weekOf(game),
				game.scheduled_date_only || '',
				game.scheduled_day_of_week || '',
				game.scheduled_time_only || '',
				home?.participant_name || 'TBD',
				isBye ? 'BYE' : (away?.participant_name || 'TBD'),
				game.location?.name || 'TBD',
				game.game_type ? game.game_type.charAt(0).toUpperCase() + game.game_type.slice(1) : '',
			].map(escapeCsv).join(',');
		});

		const csv = [header.join(','), ...rows].join('\r\n');
		// Prepend BOM so Excel opens UTF-8 correctly.
		const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		const safeName = (seasonDetails?.name || `season-${seasonId}`).replace(/[^a-z0-9]+/gi, '_');
		link.href = url;
		link.download = `${safeName}_schedule.csv`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	const handleEventAdded = (newEvent) => {
		setEvents((prevEvents) => [...prevEvents, newEvent]);
	};

	const handleEventUpdated = (updatedEvent) => {
		setEvents((prevEvents) =>
			prevEvents.map((event) =>
				event.id === updatedEvent.id ? updatedEvent : event
			)
		);
	};

	const handleEventDeleted = (eventId) => {
		setEvents((prevEvents) =>
			prevEvents.filter((event) => event.id !== eventId)
		);
	};

	const handleDeleteEvent = (eventId) => {
		if (window.confirm('Are you sure you want to delete this event?')) {
			axios
				.delete(`/api/leagues/events/${eventId}/`, {
					headers: {
						Authorization: `Bearer ${localStorage.getItem('access_token')}`,
					},
				})
				.then(() => {
					handleEventDeleted(eventId);
				})
				.catch((error) => {
					alert('Failed to delete event. Please try again.');
				});
		}
	};

	const handleSponsorAdded = (newSponsor) => {
		setSponsors((prevSponsors) => [...prevSponsors, newSponsor]);
	};

	const handleSponsorUpdated = (updatedSponsor) => {
		setSponsors((prevSponsors) =>
			prevSponsors.map((sponsor) =>
				sponsor.id === updatedSponsor.id ? updatedSponsor : sponsor
			)
		);
	};

	const handleSponsorDeleted = (sponsorId) => {
		setSponsors((prevSponsors) =>
			prevSponsors.filter((sponsor) => sponsor.id !== sponsorId)
		);
	};

	const handleDeleteSponsor = (sponsorId) => {
		if (window.confirm('Are you sure you want to delete this sponsor?')) {
			axios
				.delete(`/api/leagues/sponsors/${sponsorId}/`, {
					withCredentials: true,
				})
				.then(() => {
					handleSponsorDeleted(sponsorId);
				})
				.catch((error) => {
					alert('Failed to delete sponsor. Please try again.');
				});
		}
	};

	const renderCreateButton = () => {
		if (!isCommissioner) return null;

		switch (innerActiveTab) {
			case "Teams":
				return (
					<button className="btn btn-primary-soft" onClick={handleShow}>
						<i className="bi bi-plus pe-1"></i> Create Team
					</button>
				);
			case "Schedule":
				return (
					<div className="d-flex gap-2">
						<button
							className="btn btn-primary-soft"
							onClick={handleShowGameModal}
						>
							<i className="bi bi-plus pe-1"></i> Create Game
						</button>
						<button
							className="btn btn-success-soft"
							onClick={handleExportSchedule}
							disabled={!games.length}
							title="Download the full season schedule as a CSV for emailing"
						>
							<i className="bi bi-download pe-1"></i> Export Schedule
						</button>
						<button
							className={`btn ${seasonDetails?.schedule_lock ? 'btn-secondary' : 'btn-warning-soft'}`}
							onClick={handleShowScheduleWizard}
							disabled={seasonDetails?.schedule_lock}
							title={seasonDetails?.schedule_lock ? 'Schedule is locked - cannot regenerate' : 'Generate automated schedule'}
						>
							<i className={`bi ${seasonDetails?.schedule_lock ? 'bi-lock' : 'bi-magic'} pe-1`}></i>
							{seasonDetails?.schedule_lock ? 'Schedule Locked' : 'Generate Schedule'}
						</button>
					</div>
				);
			case "Events":
				return (
					<button
						className="btn btn-primary-soft"
						onClick={handleShowEventModal}
					>
						<i className="bi bi-plus pe-1"></i> Create Event
					</button>
				);
			case "Sponsors":
				return (
					<button
						className="btn btn-primary-soft"
						onClick={handleShowSponsorModal}
					>
						<i className="bi bi-plus pe-1"></i> Add Sponsor
					</button>
				);
			default:
				return null;
		}
	};

	return (
		// Render this page as the Leagues tab pane in your SettingsLayout (id="nav-setting-tab-6")
		<div className="tab-pane fade show active" id="nav-setting-tab-6">
			<div className="container">

				{/* Season Detail Header */}
				<div className="container ps-0 pe-0">
					<div className="card mb-3 p-0">
						<div
							className="h-200px rounded-top"
							style={{
								backgroundImage: `url(${seasonDetails.banner ||
									`/static/images/kb_static/lemont_rec_league_banner.png`
									})`,
								backgroundPosition: "center",
								backgroundSize: "cover",
								backgroundRepeat: "no-repeat",
							}}
						></div>
					</div>
				</div>

				{/* Season Details */}
				<div className="card card-body mb-3">
					<div className="row g-4">
						<div className="col-sm-6 col-lg-4">
							<h5>Start Date</h5>
							<p className="small mb-0">{seasonDetails.start_date}</p>
						</div>
						<div className="col-sm-6 col-lg-4">
							<h5>End Date</h5>
							<p className="small mb-0">{seasonDetails.end_date}</p>
						</div>
						<div className="col-sm-6 col-lg-4">
							<h5>League</h5>
							<p className="small mb-0">
								<button
									className="btn btn-link text-decoration-none p-0"
									onClick={handleBack}
								>
									{seasonDetails.league_name}
								</button>
							</p>
						</div>
						<div className="col-sm-6 col-lg-4">
							<div className="d-flex justify-content-between align-items-start">
								<h5>Registration</h5>
								{isCommissioner && (
									<button
										className="btn btn-light btn-sm rounded-circle"
										onClick={handleShowRegistrationModal}
										title="Edit Registration Settings"
									>
										<i className="bi bi-pencil"></i>
									</button>
								)}
							</div>
							<p className="small mb-1">
								{seasonDetails?.registration_open ? (
									<span className="text-success">
										<i className="bi bi-check-circle-fill me-1"></i>
										Open
									</span>
								) : (
									<span className="text-muted">
										<i className="bi bi-x-circle me-1"></i>
										Closed
									</span>
								)}
							</p>
							<p className="small mb-1">
								Start: {formatDateForDisplay(seasonDetails?.registration_start_date) || 'Not set'}
							</p>
							<p className="small mb-1">
								Deadline: {formatDateForDisplay(seasonDetails?.registration_deadline) || 'Not set'}
							</p>
							<p className="small mb-1">
								Max Teams: {seasonDetails?.max_teams ?? 'No limit'}
							</p>
							<div className="small mb-0">
								Pricing:{' '}
								{seasonDetails?.pricing_tiers?.length ? (
									<ul className="list-unstyled mb-0 ms-3">
										{seasonDetails.pricing_tiers.map((tier) => (
											<li key={tier.id}>
												{tier.name}: ${tier.fee}{' '}
												<span className="text-muted">
													({formatDateForDisplay(tier.start_date)} &ndash; {formatDateForDisplay(tier.end_date)})
												</span>
											</li>
										))}
									</ul>
								) : (
									<span className="text-muted">No tiers set</span>
								)}
							</div>
						</div>
						<div className="col-sm-6 col-lg-4">
							<div className="d-flex justify-content-between align-items-start">
								<h5>Locks</h5>
								{isCommissioner && (
									<button
										className="btn btn-light btn-sm rounded-circle"
										onClick={handleShowLocksModal}
										title="Edit Locks"
									>
										<i className="bi bi-pencil"></i>
									</button>
								)}
							</div>
							<p className="small mb-1">
								Schedule:{' '}
								{seasonDetails?.schedule_lock ? (
									<span className="text-success">
										<i className="bi bi-lock-fill me-1"></i>
										Locked
									</span>
								) : (
									<span className="text-warning">
										<i className="bi bi-unlock me-1"></i>
										Unlocked
									</span>
								)}
							</p>
							<p className="small mb-1">
								Team:{' '}
								{seasonDetails?.team_lock ? (
									<span className="text-success">
										<i className="bi bi-lock-fill me-1"></i>
										Locked
									</span>
								) : (
									<span className="text-warning">
										<i className="bi bi-unlock me-1"></i>
										Unlocked
									</span>
								)}
							</p>
							<p className="small mb-0">
								Player:{' '}
								{seasonDetails?.player_lock ? (
									<span className="text-success">
										<i className="bi bi-lock-fill me-1"></i>
										Locked
									</span>
								) : (
									<span className="text-warning">
										<i className="bi bi-unlock me-1"></i>
										Unlocked
									</span>
								)}
							</p>
						</div>
						<div className="col-sm-6 col-lg-4">
							<h5>Fields</h5>
							{seasonDetails.locations && seasonDetails.locations.length > 0 ? (
								<ul className="list-unstyled">
									{seasonDetails.locations.map((location) => (
										<li key={location.id}>
											{location.name}, {location.city}
										</li>
									))}
								</ul>
							) : (
								<p className="small mb-0">No locations available</p>
							)}
							{isCommissioner && (
								<button className="btn btn-primary mt-2" onClick={handleShowLocationModal}>
									Add Location
								</button>
							)}
						</div>
					</div>
				</div>

				{/* Inner Tab Navigation */}
				<div className="card h-100">
					<div className="card-header d-sm-flex align-items-center text-center justify-content-sm-between border-0 pb-0">
						<h1 className="h4 card-title">Season Details</h1>
						{renderCreateButton()}
					</div>

					<div className="card-body">
						<ul className="nav nav-tabs nav-bottom-line justify-content-center justify-content-md-start">
							<li className="nav-item">
								<a
									className={`nav-link ${innerActiveTab === "Teams" ? "active" : ""}`}
									href="#tab-1"
									onClick={(e) => {
										e.preventDefault();
										setInnerActiveTab("Teams");
									}}
								>
									Teams
								</a>
							</li>
							<li className="nav-item">
								<a
									className={`nav-link ${innerActiveTab === "Schedule" ? "active" : ""}`}
									href="#tab-2"
									onClick={(e) => {
										e.preventDefault();
										setInnerActiveTab("Schedule");
									}}
								>
									Schedule
								</a>
							</li>
							<li className="nav-item">
								<a
									className={`nav-link ${innerActiveTab === "Players" ? "active" : ""}`}
									href="#tab-3"
									onClick={(e) => {
										e.preventDefault();
										setInnerActiveTab("Players");
									}}
								>
									Players
								</a>
							</li>
							<li className="nav-item">
								<a
									className={`nav-link ${innerActiveTab === "Events" ? "active" : ""}`}
									href="#tab-4"
									onClick={(e) => {
										e.preventDefault();
										setInnerActiveTab("Events");
									}}
								>
									Events
								</a>
							</li>
							<li className="nav-item">
								<a
									className={`nav-link ${innerActiveTab === "Standings" ? "active" : ""}`}
									href="#tab-5"
									onClick={(e) => {
										e.preventDefault();
										setInnerActiveTab("Standings");
									}}
								>
									Standings
								</a>
							</li>
							<li className="nav-item">
								<a
									className={`nav-link ${innerActiveTab === "Sponsors" ? "active" : ""}`}
									href="#tab-6"
									onClick={(e) => {
										e.preventDefault();
										setInnerActiveTab("Sponsors");
									}}
								>
									Sponsors
								</a>
							</li>
							<li className="nav-item">
								<a
									className={`nav-link ${innerActiveTab === "Rules" ? "active" : ""}`}
									href="#tab-6"
									onClick={(e) => {
										e.preventDefault();
										setInnerActiveTab("Rules");
									}}
								>
									Rules
								</a>
							</li>
						</ul>

						{/* Inner Tab Content */}
						<div className="tab-content mb-0 pb-0">
							<div
								className={`tab-pane fade ${innerActiveTab === "Teams" ? "show active" : ""}`}
								id="tab-1"
							>
								<div className="container">
									<div className="row g-4">
										{teams.length > 0 ? (
											teams.map((team) => (
												<div key={team.id} className="col-sm-6 col-xl-4">
													<Link to={`/league-manager/teams/${team.id}`} className="text-decoration-none">
														<div className="card h-100">
															<div className="position-relative">
																<img
																	className="img-fluid rounded-top w-100"
																	src={
																		team.logo ||
																		`/static/images/kb_static/lemont_rec_leagues_circle.png`
																	}
																	alt={`${team.name} logo`}
																	style={{
																		height: "280px",
																		objectFit: "cover",
																	}}
																/>
															</div>
															<div className="card-body position-relative pt-0">
																<h6 className="mt-2 text-dark">{team.name}</h6>
																<p className="mb-1 small text-muted">
																	<i className="bi bi-trophy pe-1"></i> Wins: {team.wins}
																</p>
																<p className="mb-1 small text-muted">
																	<i className="bi bi-trophy-fill pe-1"></i> Losses: {team.losses}
																</p>
																<p className="mb-0 small text-muted">
																	<i className="bi bi-trophy-fill pe-1"></i> Draws: {team.draws}
																</p>
															</div>
														</div>
													</Link>
												</div>
											))
										) : (
											<div className="col-12">
												<p>No teams found.</p>
											</div>
										)}
									</div>
								</div>
							</div>
							<div
								className={`tab-pane fade ${innerActiveTab === "Schedule" ? "show active" : ""}`}
								id="tab-2"
							>
								<div className="container">
									{/* Filter Controls */}
									<div className="row mb-3">
										<div className="col-md-6">
											<label htmlFor="gameTypeFilter" className="form-label">Filter by Game Type:</label>
											<select
												id="gameTypeFilter"
												className="form-select"
												value={gameTypeFilter}
												onChange={(e) => setGameTypeFilter(e.target.value)}
											>
												<option value="all">All Games</option>
												<option value="preseason">Preseason</option>
												<option value="regular">Regular Season</option>
												<option value="makeup">Makeup</option>
												<option value="playoff">Playoff</option>
												<option value="bye">Bye</option>
											</select>
										</div>
										<div className="col-md-6 d-flex align-items-end">
											<small className="text-muted">
												Showing {filteredGames.length} of {games.length} games
											</small>
										</div>
									</div>

									{/* Game Type Legend */}
									<div className="row mb-3">
										<div className="col-12">
											<div className="d-flex flex-wrap gap-2 align-items-center">
												<small className="text-muted me-2">Game Types:</small>
												<span className="badge bg-info">Preseason</span>
												<span className="badge bg-primary">Regular</span>
												<span className="badge bg-warning">Makeup</span>
												<span className="badge bg-success">Playoff</span>
												<span className="badge bg-secondary">Bye</span>
											</div>
										</div>
									</div>
									<div className="row g-4">
										{filteredGames.map((game) => {
											const homeParticipant =
												game.teams?.find((p) => p.is_home) || {};
											const awayParticipant =
												game.teams?.find((p) => p.is_away) || {};
											const isByeGame = game.game_type === 'bye';

											return (
												<div key={game.id} className="col-lg-4 col-md-6 col-sm-12">
													<div
														className="card h-100 shadow-sm position-relative"
														style={{
															transition: 'all 0.3s ease',
															cursor: 'pointer'
														}}
														onMouseEnter={(e) => {
															e.currentTarget.style.transform = 'translateY(-5px)';
															e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
														}}
														onMouseLeave={(e) => {
															e.currentTarget.style.transform = 'translateY(0)';
															e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
														}}
													>
														{/* Clickable area for game details */}
														<Link to={`/league-manager/games/${game.id}/`} className="text-decoration-none">
															<div className="position-relative">
																{/* Game Type Badge - positioned above the image */}
																<div className="position-absolute top-0 start-0 m-2" style={{ zIndex: 10 }}>
																	<span className={`badge ${game.game_type === 'preseason' ? 'bg-info' :
																		game.game_type === 'regular' ? 'bg-primary' :
																			game.game_type === 'makeup' ? 'bg-warning' :
																				game.game_type === 'playoff' ? 'bg-success' : 'bg-secondary'
																		}`}>
																		{game.game_type ? game.game_type.charAt(0).toUpperCase() + game.game_type.slice(1) : 'Unknown'}
																	</span>
																</div>
																<div
																	style={{
																		width: '100%',
																		height: '200px',
																		position: 'relative',
																		overflow: 'hidden',
																		borderRadius: '8px 8px 0 0',
																	}}
																>
																	{isByeGame ? (
																		// For bye games, show only home team logo
																		homeParticipant.participant_logo ? (
																			<img
																				src={homeParticipant.participant_logo}
																				alt={`${homeParticipant.participant_name} logo`}
																				style={{
																					width: '100%',
																					height: '200px',
																					objectFit: 'cover',
																				}}
																			/>
																		) : (
																			// Placeholder for bye games
																			<div
																				style={{
																					width: '100%',
																					height: '200px',
																					backgroundColor: '#f8f9fa',
																					display: 'flex',
																					alignItems: 'center',
																					justifyContent: 'center',
																				}}
																			>
																				<i className="bi bi-people-fill" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
																			</div>
																		)
																	) : (
																		// For regular games, show split image
																		<>
																			{homeParticipant.participant_logo ? (
																				// Top half - Home Team logo
																				<img
																					src={homeParticipant.participant_logo}
																					alt={`${homeParticipant.participant_name} logo`}
																					style={{
																						width: '100%',
																						height: '200px',
																						objectFit: 'cover',
																						position: 'absolute',
																						left: 0,
																						top: 0,
																						clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)',
																					}}
																				/>
																			) : (
																				// Placeholder for top half - Blue
																				<div
																					style={{
																						width: '100%',
																						height: '50%',
																						backgroundColor: 'blue',
																						position: 'absolute',
																						top: 0,
																					}}
																				></div>
																			)}

																			{awayParticipant.participant_logo ? (
																				// Bottom half - Away Team logo
																				<img
																					src={awayParticipant.participant_logo}
																					alt={`${awayParticipant.participant_name} logo`}
																					style={{
																						width: '100%',
																						height: '200px',
																						objectFit: 'cover',
																						position: 'absolute',
																						left: 0,
																						bottom: 0,
																						clipPath: 'polygon(0 50%, 100% 50%, 100% 100%, 0 100%)',
																					}}
																				/>
																			) : (
																				// Placeholder for bottom half - Red
																				<div
																					style={{
																						width: '100%',
																						height: '50%',
																						backgroundColor: 'red',
																						position: 'absolute',
																						bottom: 0,
																					}}
																				></div>
																			)}
																		</>
																	)}
																</div>
															</div>
															<div className="card-body p-3">
																{isByeGame ? (
																	// Simplified display for bye games
																	<div className="text-center mb-2">
																		<h6 className="mb-1 text-dark fw-bold">
																			{homeParticipant.participant_name || 'Team'}
																		</h6>
																		<div className="text-muted small mb-1">(BYE)</div>
																	</div>
																) : (
																	// Regular game display
																	<div className="text-center mb-2">
																		<h6 className="mb-1 text-dark fw-bold">
																			{homeParticipant.participant_name || 'Home TBD'}
																		</h6>
																		<div className="text-muted small mb-1">vs</div>
																		<h6 className="mb-0 text-dark fw-bold">
																			{awayParticipant.participant_name || 'Away TBD'}
																		</h6>
																	</div>
																)}
																<hr className="my-2" />
																{!isByeGame && (
																	<div className="small text-muted">
																		<div className="mb-1">
																			<i className="bi bi-calendar-check me-2"></i>
																			{game.scheduled_date_only}
																		</div>
																		<div className="mb-1">
																			<i className="bi bi-clock me-2"></i>
																			{game.scheduled_time_only}
																		</div>
																		<div>
																			<i className="bi bi-geo-alt me-2"></i>
																			{game.location?.name || 'TBD'}
																		</div>
																	</div>
																)}
																{/* Add empty space for bye games to maintain card height */}
																{isByeGame && (
																	<div style={{ height: '60px' }}></div>
																)}
															</div>
														</Link>

														{/* Edit Button - positioned in lower right corner */}
														{isCommissioner && (
															<button
																className="btn btn-sm btn-outline-primary position-absolute"
																style={{
																	bottom: '8px',
																	right: '8px',
																	zIndex: 20,
																	width: '32px',
																	height: '32px',
																	padding: '0',
																	borderRadius: '50%',
																	backgroundColor: 'rgba(255, 255, 255, 0.9)',
																	border: '1px solid #007bff'
																}}
																onClick={(e) => {
																	e.preventDefault();
																	e.stopPropagation();
																	handleShowEditGameModal(game);
																}}
																title="Edit Game"
															>
																<i className="bi bi-pencil" style={{ fontSize: '12px' }}></i>
															</button>
														)}
													</div>
												</div>
											);
										})}
									</div>
								</div>
							</div>
							<div
								className={`tab-pane fade ${innerActiveTab === "Players" ? "show active" : ""}`}
								id="tab-3"
							>
								<div className="container">
									{/* Search and Controls */}
									<div className="row mb-4">
										<div className="col-md-6">
											<div className="input-group">
												<span className="input-group-text">
													<i className="bi bi-search"></i>
												</span>
												<input
													type="text"
													className="form-control"
													placeholder="Search players..."
													value={searchTerm}
													onChange={handleSearch}
													autoComplete="off"
												/>
											</div>
										</div>
										<div className="col-md-3">
											<select
												className="form-select"
												value={pageSize}
												onChange={handlePageSizeChange}
											>
												<option value={5}>5 per page</option>
												<option value={10}>10 per page</option>
												<option value={20}>20 per page</option>
												<option value={30}>30 per page</option>
											</select>
										</div>
										<div className="col-md-3">
											<div className="text-end">
												<small className="text-muted">
													Showing {totalCount} players
												</small>
											</div>
										</div>
									</div>

									{/* Players Grid */}
									<div className="row g-4">
										{getCurrentPagePlayers().map((player) => (
											<div key={player.id} className="col-12 col-sm-6">
												<div className="card h-100">
													<div className="card-body">
														<div className="d-flex flex-column">
															<div className="d-flex align-items-center mb-3">
																<img
																	src={player.profile_picture || `/static/images/kb_static/lemont_rec_leagues_circle.png`}
																	alt={`${player.first_name} ${player.last_name}`}
																	className="rounded-circle me-3"
																	style={{ width: '60px', height: '60px', objectFit: 'cover' }}
																/>
																<div className="flex-grow-1">
																	<h6 className="mb-1">
																		{truncateString(player.first_name + ' ' + player.last_name, 20)}
																	</h6>
																	<small className="text-muted">
																		{player.team ? `Team: ${player.team}` : 'Free Agent'}
																	</small>
																</div>
															</div>
															<div className="mt-auto">
																{player.email && (
																	<div className="mb-2">
																		<small className="text-muted">
																			<i className="bi bi-envelope me-1"></i>
																			<a href={`mailto:${player.email}`} className="text-decoration-none">
																				{truncateString(player.email, 35)}
																			</a>
																		</small>
																	</div>
																)}
																{player.phone_number && (
																	<div className="mb-2">
																		<small className="text-muted">
																			<i className="bi bi-telephone me-1"></i>
																			<a href={`tel:${player.phone_number}`} className="text-decoration-none">
																				{truncateString(player.phone_number, 15)}
																			</a>
																		</small>
																	</div>
																)}
															</div>
														</div>
													</div>
												</div>
											</div>
										))}
										{getCurrentPagePlayers().length === 0 && (
											<div className="col-12">
												<div className="text-center text-muted py-4">
													{searchTerm ? 'No players found matching your search' : 'No players found'}
												</div>
											</div>
										)}
									</div>

									{/* Mobile-Friendly Pagination */}
									{totalPages > 1 && (
										<div className="d-flex flex-column align-items-center mt-4">
											<div className="text-center mb-3">
												<span className="text-muted">
													Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} players
												</span>
											</div>
											<nav aria-label="Page navigation">
												<ul className="pagination pagination-sm mb-0">
													<li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
														<button
															className="page-link"
															onClick={() => handlePageChange(currentPage - 1)}
															disabled={currentPage === 1}
														>
															<i className="bi bi-chevron-left"></i>
														</button>
													</li>

													{/* Show page numbers with ellipsis for mobile */}
													{totalPages <= 5 ? (
														// Show all pages if 5 or fewer
														[...Array(totalPages)].map((_, index) => {
															const pageNum = index + 1;
															return (
																<li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
																	<button
																		className="page-link"
																		onClick={() => handlePageChange(pageNum)}
																	>
																		{pageNum}
																	</button>
																</li>
															);
														})
													) : (
														// Show smart pagination for mobile
														<>
															{/* First page */}
															<li className={`page-item ${currentPage === 1 ? 'active' : ''}`}>
																<button
																	className="page-link"
																	onClick={() => handlePageChange(1)}
																>
																	1
																</button>
															</li>

															{/* Ellipsis if needed */}
															{currentPage > 3 && (
																<li className="page-item disabled">
																	<span className="page-link">...</span>
																</li>
															)}

															{/* Current page and neighbors */}
															{currentPage > 2 && currentPage < totalPages && (
																<li className="page-item">
																	<button
																		className="page-link"
																		onClick={() => handlePageChange(currentPage - 1)}
																	>
																		{currentPage - 1}
																	</button>
																</li>
															)}

															{currentPage !== 1 && currentPage !== totalPages && (
																<li className="page-item active">
																	<button className="page-link">
																		{currentPage}
																	</button>
																</li>
															)}

															{currentPage < totalPages - 1 && currentPage > 1 && (
																<li className="page-item">
																	<button
																		className="page-link"
																		onClick={() => handlePageChange(currentPage + 1)}
																	>
																		{currentPage + 1}
																	</button>
																</li>
															)}

															{/* Ellipsis if needed */}
															{currentPage < totalPages - 2 && (
																<li className="page-item disabled">
																	<span className="page-link">...</span>
																</li>
															)}

															{/* Last page */}
															<li className={`page-item ${currentPage === totalPages ? 'active' : ''}`}>
																<button
																	className="page-link"
																	onClick={() => handlePageChange(totalPages)}
																>
																	{totalPages}
																</button>
															</li>
														</>
													)}

													<li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
														<button
															className="page-link"
															onClick={() => handlePageChange(currentPage + 1)}
															disabled={currentPage === totalPages}
														>
															<i className="bi bi-chevron-right"></i>
														</button>
													</li>
												</ul>
											</nav>
										</div>
									)}
								</div>
							</div>
							<div
								className={`tab-pane fade ${innerActiveTab === "Events" ? "show active" : ""}`}
								id="tab-4"
							>
								<div className="container">
									<div className="row g-4">
										{events.length > 0 ? (
											events.map((event) => (
												<div key={event.id} className="col-lg-4 col-md-6 col-sm-12">
													<div className="card h-100 shadow-sm">
														<div className="card-body">
															<div className="d-flex justify-content-between align-items-start mb-2">
																<h6 className="card-title mb-0">{event.title}</h6>
																{!event.is_public && (
																	<span className="badge bg-secondary">Private</span>
																)}
															</div>
															<div className="small text-muted">
																<div className="mb-1">
																	<i className="bi bi-calendar-event me-2"></i>
																	{event.formatted_datetime}
																</div>
																<div className="mb-1">
																	<i className="bi bi-geo-alt me-2"></i>
																	{event.location_name && (
																		<span className="fw-semibold">{event.location_name}</span>
																	)}
																	{event.location_name && event.address_line1 && <br />}
																	{event.address_line1 && (
																		<span>{event.address_line1}, {event.city}</span>
																	)}
																</div>
																{event.team_name && (
																	<div className="mb-1">
																		<i className="bi bi-people me-2"></i>
																		{event.team_name}
																	</div>
																)}
																{event.sponsor && (
																	<div className="mb-1 d-flex align-items-center">
																		{/* Sponsor Logo - Small and responsive */}
																		{event.sponsor.logo_url ? (
																			<div
																				className="me-2"
																				style={{
																					width: '24px',
																					height: '24px',
																					minWidth: '24px',
																					position: 'relative',
																					overflow: 'hidden',
																					borderRadius: '3px',
																					backgroundColor: '#f8f9fa',
																					border: '1px solid #dee2e6',
																				}}
																			>
																				<img
																					src={event.sponsor.logo_url}
																					alt={`${event.sponsor.name} logo`}
																					style={{
																						width: '100%',
																						height: '100%',
																						objectFit: 'contain',
																						padding: '1px',
																					}}
																					onError={(e) => {
																						// Hide the image and show the fallback icon if the image fails to load
																						e.target.style.display = 'none';
																						e.target.nextSibling.style.display = 'flex';
																					}}
																				/>
																				<div
																					className="d-flex align-items-center justify-content-center h-100"
																					style={{
																						display: 'none',
																						fontSize: '10px',
																						color: '#6c757d'
																					}}
																				>
																					<i className="bi bi-award"></i>
																				</div>
																			</div>
																		) : (
																			<i className="bi bi-award me-2"></i>
																		)}
																		Sponsored by {event.sponsor.name}
																	</div>
																)}
																<div>
																	<i className="bi bi-person me-2"></i>
																	Created by {event.created_by_name}
																</div>
															</div>
														</div>
														{isCommissioner && (
															<div className="card-footer bg-transparent border-0">
																<div className="d-flex gap-2">
																	<button
																		className="btn btn-sm btn-outline-primary"
																		onClick={() => handleShowEditEventModal(event)}
																	>
																		<i className="bi bi-pencil"></i> Edit
																	</button>
																	<button
																		className="btn btn-sm btn-outline-danger"
																		onClick={() => handleDeleteEvent(event.id)}
																	>
																		<i className="bi bi-trash"></i> Delete
																	</button>
																</div>
															</div>
														)}
													</div>
												</div>
											))
										) : (
											<div className="col-12">
												<div className="text-center py-5">
													<i className="bi bi-calendar-event display-1 text-muted"></i>
													<h5 className="mt-3 text-muted">No Events Yet</h5>
													<p className="text-muted">Create your first event to get started!</p>
												</div>
											</div>
										)}
									</div>
								</div>
							</div>
							<div
								className={`tab-pane fade ${innerActiveTab === "Sponsors" ? "show active" : ""}`}
								id="tab-5"
							>
								<div className="container">
									<div className="row g-4">
										{sponsors.length > 0 ? (
											sponsors.map((sponsor) => (
												<div key={sponsor.id} className="col-lg-4 col-md-6 col-sm-12">
													<div className="card h-100 shadow-sm">
														<div className="card-body">
															<div className="d-flex justify-content-between align-items-start mb-2">
																<h6 className="card-title mb-0">{sponsor.name}</h6>
																{!sponsor.is_active && (
																	<span className="badge bg-secondary">Inactive</span>
																)}
															</div>
															<div className="small text-muted">
																<div className="mb-1">
																	<i className="bi bi-star me-2"></i>
																	{sponsor.tier_name}
																</div>
																{sponsor.website && (
																	<div className="mb-1">
																		<i className="bi bi-globe me-2"></i>
																		<a href={sponsor.website} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
																			{sponsor.website}
																		</a>
																	</div>
																)}
																{sponsor.amount_contributed && (
																	<div className="mb-1">
																		<i className="bi bi-currency-dollar me-0"></i>
																		{sponsor.amount_contributed}
																	</div>
																)}
																{sponsor.description && (
																	<div className="mb-1">
																		<i className="bi bi-info-circle me-2"></i>
																		{sponsor.description}
																	</div>
																)}
																<div>
																	<i className="bi bi-eye me-2"></i>
																	{sponsor.display_on_website ? 'Displayed on website' : 'Hidden from website'}
																</div>
															</div>
														</div>
														{isCommissioner && (
															<div className="card-footer bg-transparent border-0">
																<div className="d-flex gap-2">
																	<button
																		className="btn btn-sm btn-outline-primary"
																		onClick={() => handleShowEditSponsorModal(sponsor)}
																	>
																		<i className="bi bi-pencil"></i> Edit
																	</button>
																	<button
																		className="btn btn-sm btn-outline-danger"
																		onClick={() => handleDeleteSponsor(sponsor.id)}
																	>
																		<i className="bi bi-trash"></i> Delete
																	</button>
																</div>
															</div>
														)}
													</div>
												</div>
											))
										) : (
											<div className="col-12">
												<div className="text-center py-5">
													<i className="bi bi-star display-1 text-muted"></i>
													<h5 className="mt-3 text-muted">No Sponsors Yet</h5>
													<p className="text-muted">Add your first sponsor to get started!</p>
												</div>
											</div>
										)}
									</div>
								</div>
							</div>
							<div
								className={`tab-pane fade ${innerActiveTab === "Standings" ? "show active" : ""}`}
								id="tab-5"
							>
								<div className="container">
									{teams.length > 0 ? (
										<div className="table-responsive">
											<table className="table table-hover">
												<thead className="table-light">
													<tr>
														<th scope="col" className="text-center">Rank</th>
														<th scope="col">Team</th>
														<th scope="col" className="text-center">W</th>
														<th scope="col" className="text-center">L</th>
														<th scope="col" className="text-center">D</th>
														<th scope="col" className="text-center">Win %</th>
													</tr>
												</thead>
												<tbody>
													{teams
														.sort((a, b) => {
															// Primary sort: wins (descending)
															if (b.wins !== a.wins) {
																return b.wins - a.wins;
															}
															// Secondary sort: win percentage (descending)
															const aTotalGames = a.wins + a.losses + a.draws;
															const bTotalGames = b.wins + b.losses + b.draws;
															const aWinPercentage = aTotalGames > 0 ? a.wins / aTotalGames : 0;
															const bWinPercentage = bTotalGames > 0 ? b.wins / bTotalGames : 0;
															return bWinPercentage - aWinPercentage;
														})
														.map((team, index) => {
															const totalGames = team.wins + team.losses + team.draws;
															const winPercentage = totalGames > 0
																? ((team.wins / totalGames) * 100).toFixed(1)
																: '0.0';

															return (
																<tr key={team.id}>
																	<td className="text-center fw-bold">
																		{index + 1}
																	</td>
																	<td>
																		<div className="d-flex align-items-center">
																			<img
																				src={team.logo || `/static/images/kb_static/lemont_rec_leagues_circle.png`}
																				alt={`${team.name} logo`}
																				className="rounded-circle me-3"
																				style={{ width: '40px', height: '40px', objectFit: 'cover' }}
																			/>
																			<span className="fw-semibold">{team.name}</span>
																		</div>
																	</td>
																	<td className="text-center text-success fw-bold">{team.wins}</td>
																	<td className="text-center text-danger">{team.losses}</td>
																	<td className="text-center text-warning">{team.draws}</td>
																	<td className="text-center fw-semibold">{winPercentage}%</td>
																</tr>
															);
														})}
												</tbody>
											</table>
										</div>
									) : (
										<div className="text-center py-5">
											<i className="bi bi-trophy display-1 text-muted"></i>
											<h5 className="mt-3 text-muted">No Teams Yet</h5>
											<p className="text-muted">No teams have been created for this season.</p>
										</div>
									)}
								</div>
							</div>
							<div
								className={`tab-pane fade ${innerActiveTab === "Rules" ? "show active" : ""}`}
								id="tab-6"
							>
								<div>Rules content goes here</div>
							</div>
						</div>
					</div>
				</div>

				<AddTeamModal
					show={modalVisible}
					handleClose={handleClose}
					onTeamAdded={handleTeamAdded}
					requireCaptain={false}
					leagueId={seasonDetails?.league}
					seasonId={seasonId}
				/>
				<AddLocationModal
					show={showLocationModal}
					handleClose={handleCloseLocationModal}
					onLocationAdded={handleLocationAdded}
					seasonId={seasonId}
				/>
				<AddGameModal
					show={showGameModal}         // Assume you have state for controlling Game modal visibility
					handleClose={handleCloseGameModal}  // Your function to close the modal
					onGameAdded={handleGameAdded} // Callback to update the game list
					seasonId={seasonId}           // Pass the seasonId here
				/>
				<SeasonScheduleWizard
					show={showScheduleWizard}
					handleClose={handleCloseScheduleWizard}
					onScheduleGenerated={handleScheduleGenerated}
					seasonId={seasonId}
					scheduleLocked={seasonDetails?.schedule_lock}
				/>
				<EditGameModal
					show={showEditGameModal}
					handleClose={handleCloseEditGameModal}
					game={selectedGame}
					onGameUpdated={handleGameUpdated}
					seasonId={seasonId}
				/>
				<AddEventModal
					show={showEventModal}
					handleClose={handleCloseEventModal}
					onEventAdded={handleEventAdded}
					seasonId={seasonId}
				/>
				<EditEventModal
					show={showEditEventModal}
					handleClose={handleCloseEditEventModal}
					onEventUpdated={handleEventUpdated}
					event={selectedEvent}
					seasonId={seasonId}
				/>
				<AddSponsorModal
					show={showSponsorModal}
					handleClose={handleCloseSponsorModal}
					onSponsorAdded={handleSponsorAdded}
					seasonId={seasonId}
				/>
				<EditSponsorModal
					show={showEditSponsorModal}
					handleClose={handleCloseEditSponsorModal}
					onSponsorUpdated={handleSponsorUpdated}
					sponsor={selectedSponsor}
					seasonId={seasonId}
				/>
				<EditRegistrationModal
					show={showRegistrationModal}
					handleClose={handleCloseRegistrationModal}
					season={seasonDetails}
					onSeasonUpdated={handleSeasonSettingsUpdated}
				/>
				<EditSeasonLocksModal
					show={showLocksModal}
					handleClose={handleCloseLocksModal}
					season={seasonDetails}
					onSeasonUpdated={handleSeasonSettingsUpdated}
				/>
			</div>
		</div>
	);
}

export default SeasonDetailPage;
